// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { WorkflowRuns } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { WebSiteManagementClient } from "../webSiteManagementClient.js";
import type {
  WorkflowRun,
  WorkflowRunsListNextOptionalParams,
  WorkflowRunsListOptionalParams,
  WorkflowRunsListResponse,
  WorkflowRunsGetOptionalParams,
  WorkflowRunsGetResponse,
  WorkflowRunsCancelOptionalParams,
  WorkflowRunsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing WorkflowRuns operations. */
export class WorkflowRunsImpl implements WorkflowRuns {
  private readonly client: WebSiteManagementClient;

  /**
   * Initialize a new instance of the class WorkflowRuns class.
   * @param client Reference to the service client
   */
  constructor(client: WebSiteManagementClient) {
    this.client = client;
  }

  /**
   * Gets a list of workflow runs.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowRunsListOptionalParams,
  ): PagedAsyncIterableIterator<WorkflowRun> {
    const iter = this.listPagingAll(resourceGroupName, name, workflowName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, name, workflowName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowRunsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<WorkflowRun[]> {
    let result: WorkflowRunsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, name, workflowName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        name,
        workflowName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowRunsListOptionalParams,
  ): AsyncIterableIterator<WorkflowRun> {
    for await (const page of this.listPagingPage(resourceGroupName, name, workflowName, options)) {
      yield* page;
    }
  }

  /**
   * Gets a list of workflow runs.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    options?: WorkflowRunsListOptionalParams,
  ): Promise<WorkflowRunsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, options },
      listOperationSpec,
    );
  }

  /**
   * Gets a workflow run.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param runName The workflow run name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    runName: string,
    options?: WorkflowRunsGetOptionalParams,
  ): Promise<WorkflowRunsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, runName, options },
      getOperationSpec,
    );
  }

  /**
   * Cancels a workflow run.
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param runName The workflow run name.
   * @param options The options parameters.
   */
  cancel(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    runName: string,
    options?: WorkflowRunsCancelOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, runName, options },
      cancelOperationSpec,
    );
  }

  /**
   * ListNext
   * @param resourceGroupName Name of the resource group to which the resource belongs.
   * @param name Site name.
   * @param workflowName The workflow name.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    name: string,
    workflowName: string,
    nextLink: string,
    options?: WorkflowRunsListNextOptionalParams,
  ): Promise<WorkflowRunsListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, name, workflowName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Web/sites/{name}/hostruntime/runtime/webhooks/workflow/api/management/workflows/{workflowName}/runs",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowRunListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.top1, Parameters.filter1],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.workflowName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Web/sites/{name}/hostruntime/runtime/webhooks/workflow/api/management/workflows/{workflowName}/runs/{runName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowRun,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.workflowName1,
    Parameters.runName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const cancelOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Web/sites/{name}/hostruntime/runtime/webhooks/workflow/api/management/workflows/{workflowName}/runs/{runName}/cancel",
  httpMethod: "POST",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.workflowName1,
    Parameters.runName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.WorkflowRunListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.name,
    Parameters.nextLink,
    Parameters.workflowName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
